import "./KiboxAssignedList.scss";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Space, notification } from "antd";
import { FlagOutlined } from "@ant-design/icons";
import service from "../../services/Service";
import { hasPrivilege } from "../../api/token";
import dayjs from 'dayjs';
import { withLocale } from "../../l10n";
import DataTable from "../DataTable/DataTable";
import Excelexport from "../../utils/excelexport";
import { buildKiboxAssignedExportRows } from './KiboxAssignedExport';
import { formatDate, compareDates } from '../../utils/dates';
import { GetColumnLiteralSearchProps, getRouterLabel } from '../../utils/functions';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery, useGetTenantsLookupQuery, useGetRoutersLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function KiboxAssignedList({ lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: [],
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();
  const { data: routers = EMPTY_ARRAY } = useGetRoutersLookupQuery();

  // ===== 3. DERIVADOS =====
  const kiboxModels = models.filter(m => ['kibox', 'kibox_legacy'].includes(m.device_type));

  const tenantsFilter = tenants.map(tenant => ({ text: tenant.name, value: tenant.uuid }));

  const modelFilter = kiboxModels.map(m => ({ text: m.name, value: m.uuid }));

  const versionFilter = kiboxModels
    .map(m => ({
      text: m.name,
      value: m.uuid,
      children: versions
        .filter(v => v.model_uuid === m.uuid)
        .map(v => ({ text: v.name, value: v.uuid }))
    }))
    .filter(group => group.children.length > 0);

  const routerById = useMemo(
    () => new Map(routers.map(router => [router.uuid, router])),
    [routers]
  );

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetchBoards = useCallback(async () => {
    try{
      const res = await service.get("boards/manufacture/Asignado");

      if (res.status === 200) {
        updateData('raw', res.data);
      }
    } catch (err) {
      console.log("Error devuelto: " + err);
    }
  }, []);

  /**
   * Calculo de los datos enriquecidos para la lista de Kibox asignados
   */
  const enrichedData = useMemo(() => {
    return data.raw.map((element) => {
      const enriched = { ...element };

      const tenant = tenants.find((item) => item.uuid === element.tenant_uuid);
      if (element.tenant_uuid && tenant) {
        enriched.tenant_name = tenant.name;
      } else {
        enriched.tenant_name = lang.handle("Sin organización");
      }

      const version = versions.find(v => v.uuid === element.version_uuid);
      if (element.version_uuid && version) {
        enriched.version_name = version.name;

        const model = models.find(item => item.uuid === version.model_uuid);
        if (model) {
          enriched.model_name = model.name;
        } else {
          enriched.model_name = lang.handle("Sin modelo");
        }
      } else {
        enriched.version_name = lang.handle("Sin versión");
      }

      enriched.router_name = element.router_uuid
        ? (getRouterLabel(routerById.get(element.router_uuid)) || lang.handle('Router no disponible'))
        : (element.ki_router_sn || '');

      return enriched;
    });
  }, [data.raw, models, versions, tenants, routerById, lang]);

  useEffect(() => {
    const fetchAll = async () => {
      updateUI('loading', true);
      await fetchBoards();
      updateUI('loading', false);
    }

    fetchAll();
  }, [fetchBoards]);

  const columns = [
    {
      title: 'Id',
      dataIndex: "ki_id",
      key: 'ki_id',
      ...GetColumnLiteralSearchProps("ki_id", lang),
    },
    {
      title: lang.handle('Pedido'),
      dataIndex: "order_id",
      key: 'order_id',
      ...GetColumnLiteralSearchProps("order_id", lang),
    },
    {
      title: lang.handle('Instalación'),
      dataIndex: "name",
      key: 'name',
      ...GetColumnLiteralSearchProps("name", lang),
    },
    {
      title: lang.handle('Organización'),
      dataIndex: "tenant_name",
      key: 'tenant_name',
      filters: tenantsFilter,
      onFilter: (value, record) => record.tenant_uuid === value,
    },
    {
      title: lang.handle('Cliente final'),
      dataIndex: "final_client",
      key: 'final_client',
      ...GetColumnLiteralSearchProps("final_client", lang),
    },
    {
      title: lang.handle('UUID'),
      dataIndex: "uuid",
      key: 'uuid',
      ...GetColumnLiteralSearchProps("uuid", lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: "serial_number",
      key: 'serial_number',
      ...GetColumnLiteralSearchProps("serial_number", lang),
    },
    {
      title: lang.handle('Orden de trabajo'),
      dataIndex: "work_order",
      key: 'work_order',
      ...GetColumnLiteralSearchProps("work_order", lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "date",
      key: 'date',
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
      render: (data) => formatDate(data),
    },
    {
      title: "MAC / IMEI",
      dataIndex: "physical_id",
      key: 'physical_id',
      ...GetColumnLiteralSearchProps("physical_id", lang),
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: "model_name",
      key: 'model_name',
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
      sorter: (a, b) => (a.model_name || '').localeCompare(b.model_name || ''),
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      key: 'version_name',
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || ''),
    },
    {
      title: "ICC",
      dataIndex: "icc",
      key: 'icc',
      ...GetColumnLiteralSearchProps("icc", lang),
    },
    {
      title: lang.handle('Router kiconex'),
      dataIndex: "router_name",
      key: 'router_name',
      ...GetColumnLiteralSearchProps("router_name", lang),
    },
    {
      title: lang.handle('Fecha de expedición'),
      dataIndex: "expedition_date",
      key: 'expedition_date',
      sorter: (a, b) => compareDates(a.expedition_date, b.expedition_date),
      sortDirections: ["descend", "ascend"],
      render: (data) => formatDate(data),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: "notes",
      key: 'notes',
    },
  ];  

  const rowSelection = {
    type: 'checkbox',
    onChange: (selectedRowKeys, selectedRows) => {
      updateData('selected', selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  const finishBoard = async (e) => {
    updateUI('loading', true);
    e.preventDefault();
    for (const id of data.selected.map(row => row.ki_id)) {
      try {
        const res = await service.post("boards/finish/" + id);
        if (res && res.status === 200) {
          notification["success"]({
            message: lang.handle('Placa terminada'),
          });
        }
      } catch {
        notification["error"]({
          message: lang.handle('No se ha podido terminar'),
        });
      }
    }
    updateData('selected', []);
    await fetchBoards();
    updateUI('loading', false);
  };

  /**
   * Recopila la información necesaria para el Excel y delega su construcción
   * en el helper. Devuelve null para abortar la descarga si algo no cuadra.
   */
  const getExportData = async () => {

    const needsRouters = data.selected.some(row => row.router_uuid);
    const res = needsRouters ? await service.get("routers/manufacture/Asignado") : null;

    const rows = buildKiboxAssignedExportRows(data.selected, res?.data?.data ?? []);

    if (!rows) {
      notification["error"]({
        message: lang.handle('No se ha podido exportar el Excel'),
      });
      return null;
    }

    return rows;
  };

  let selection = null;
  if (hasPrivilege('assign_finish')) {
    selection = rowSelection;
  }

  return (
    <div>
      <DataTable
        rowSelection={selection}
        columns={columns}
        rowKey="ki_id"
        dataSource={enrichedData}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('assign_finish') && <Button
            type="primary"
            icon={<FlagOutlined />}
            disabled={data.selected.length === 0}
            onClick={finishBoard}
          >
            {lang.handle('Terminar')}
          </Button>}
        </Space>
        <Space>
          <Excelexport
            fileName={'assigned_kibox_' + dayjs().format('YYYYMMDD_HHmmss')}
            fileData={async () => {
              const rows = await getExportData();
              return rows && [{ name: 'kibox_asignados', data: rows }];
            }}
            disabled={data.selected.length === 0}
            label={lang.handle('Exportar a Excel')}
          />
        </Space>
      </div>
    </div>
  );

}

export default (withLocale(KiboxAssignedList));
